import { Component, OnInit, Inject, ViewChild, ElementRef, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { firstValueFrom, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { TokenStorageService } from '../../services/token-storage.service';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorhandlingService } from 'src/app/services/errorhandling.service';
import { UserDTO } from 'src/app/interfaces/user';
import { NoteDTO } from 'src/app/interfaces/note';
import { createMail, MailWriteDTO } from 'src/app/interfaces/mail';
import { MailService } from 'src/app/services/mail.service';
import { MatIconModule } from '@angular/material/icon';
import { MatError, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { CommonModule, NgIf } from '@angular/common';
import { environment } from 'src/environments/environment';

// Tiptap Imports
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Image as TipTapImage } from '@tiptap/extension-image';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { CustomDiv } from 'src/app/extensions/customDiv';
import { CustomParagraph } from 'src/app/extensions/CustomParagraph';

@Component({
  selector: 'app-send-mail',
  standalone: true,
  imports: [
    MatIconModule, 
    MatError, 
    FormsModule, 
    MatLabel, 
    MatInputModule, 
    ReactiveFormsModule, 
    MatFormFieldModule, 
    MatToolbarModule, 
    NgIf, 
    CommonModule
  ],
  templateUrl: './send-mail.component.html',
  styleUrls: ['./send-mail.component.scss'],
})
export class SendMailComponent implements OnInit, AfterViewInit {

  @ViewChild('editorElement') set editorElement(element: ElementRef<HTMLDivElement>) {
    if (element && !this.editor) {
      this.initializeEditor(element.nativeElement);
    } else if (element && this.editor) {
      this.editor.commands.setContent(this.form.body || '');
    }
  }

  public editor!: Editor;
  envClass = 'prod';

  isLoggedIn: boolean;
  user: UserDTO;
  role: string;
  isOk: boolean;
  destroy$: Subject<boolean> = new Subject<boolean>();
  note: NoteDTO;
  title: string;
  mail: MailWriteDTO;
  mailForm: FormGroup;
  form: any = {
    recipients: null,
    subject: null,
    body: null
  };

  constructor(
    private router: Router, 
    private mailService: MailService, 
    private tokenStorageService: TokenStorageService, 
    public dialogRef: MatDialogRef<SendMailComponent>, 
    @Inject(MAT_DIALOG_DATA) data, 
    private errorhandling: ErrorhandlingService,
    private cdr: ChangeDetectorRef
  ) {
    this.note = data;
    this.mailForm = new FormGroup({
      recipients: new FormControl(this.form.recipients, [
        Validators.required, this.commaSepEmail
      ]),
      subject: new FormControl(this.form.subject, [Validators.required]),
      body: new FormControl(this.form.body)
    });
  }

  isAuthorized() {
    this.isLoggedIn = !!this.tokenStorageService.getToken();
    if (this.isLoggedIn) {
      this.user = this.tokenStorageService.getUser();
      this.role = this.user.role;
      this.isOk = true;
    } else {
      this.isOk = false;
    }
    if (!this.isOk) {
      this.router.navigate(['home']);
    }
  }

ngOnInit(): void {
    this.envClass = `env-${environment.name}`;
    this.isAuthorized();

    this.title = this.note.title;

    let initialSubject = this.note.title;
    let initialBody = this.note.stringText;
    let initialRecipients = '';

    if (this.note.noteType == "meeting") {
      initialSubject = "Meeting minutes for: " + this.note.title;

      if (this.note.hiddenTag != null && this.note.hiddenTag != "") {
        if (this.note.stringText.indexOf(this.note.hiddenTag) > 0) {
          initialBody = this.note.stringText.substring(0, this.note.stringText.indexOf(this.note.hiddenTag));
        }
      }
      initialRecipients = this.note.participants || '';
    }

    this.mailForm.patchValue({
      recipients: initialRecipients,
      subject: initialSubject,
      body: initialBody
    });
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  initializeEditor(element: HTMLElement) {
    this.editor = new Editor({
      element: element,
      extensions: [
        StarterKit.configure({
          paragraph: false,
          heading: {
            HTMLAttributes: {
              style: null,
            },
          },
        }),
        TextStyle,
        CustomDiv,
        CustomParagraph,
        TipTapImage,
        TableRow,
        TableHeader,
        TableCell,
        TextAlign.configure({ types: ['heading', 'paragraph'] })
      ],
      content: this.form.body || '',
      onUpdate: ({ editor }) => {
        this.form.body = editor.getHTML();
      },
    });
    this.cdr.detectChanges();
  }

  commaSepEmail = (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value != null) {
      const emails = control.value.split(',');
      const forbidden = emails.some(email => Validators.email(new FormControl(email.trim())));
      return forbidden ? { 'recipients': { value: control.value } } : null;
    } else return null;
  };

  ngOnDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

async sendMail() {
    if (this.mailForm.valid) {
      const { recipients, subject, body } = this.mailForm.value;
      this.mail = createMail(recipients, subject, body || this.form.body, "NEW");
      try {
        await firstValueFrom(this.mailService.sendMail(this.mail));
        this.dialogRef.close();
      } catch (error) {
        this.errorhandling.logToConsole(error);
      }
    }
  }
  
  get recipients() { return this.mailForm.get('recipients'); }
  get subject() { return this.mailForm.get('subject'); }
  get body() { return this.mailForm.get('body'); }
}